import {
    DateField,
    EditButton,
    ReferenceField,
    Show,
    TextField
} from '@/components/admin';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, CheckCircle2, PackageCheck, Receipt, ShieldCheck, ShieldX, Truck } from 'lucide-react';
import { useGetList, useNotify, useRecordContext, useRefresh, useUpdate } from 'ra-core';
import { Link, useNavigate } from 'react-router-dom';
import { StatusBadge } from '../documents/shared/StatusBadge';
import type { GoodsReceipt } from '../types';
import { GRNItemsList } from './GRNItemsList';


const ShowTitle = () => {
    const record = useRecordContext();
    if (!record) return null;
    return (
        <div className="flex items-center gap-3">
            <Button asChild variant="outline" size="sm" className="gap-1.5 bg-primary/10 hover:bg-primary/20 text-primary border-primary/20 font-semibold cursor-pointer h-9 px-4 rounded-lg">
                <Link to="/goods_receipts" className="flex items-center gap-1.5">
                    <ArrowLeft className="w-4 h-4" /> Back to Goods Receipts
                </Link>
            </Button>
            <span className="text-xl font-bold tracking-tight text-slate-900 ml-1">{record.reference}</span>
        </div>
    );
};

const GRNShowActions = () => {
    const record = useRecordContext<GoodsReceipt>();
    const notify = useNotify();
    const refresh = useRefresh();
    const navigate = useNavigate();
    const [update, { isPending }] = useUpdate();

    // Gate: a GRN should be billed once. Disable "Create Vendor Bill" if a bill
    // already references it.
    const { data: existingBills } = useGetList(
        'vendor_bills',
        { filter: { grn_id: record?.id }, pagination: { page: 1, perPage: 1 } },
        { enabled: !!record?.id },
    );
    const hasBill = (existingBills?.length ?? 0) > 0;

    if (!record) return null;

    const handleStatusChange = (newStatus: string, label: string) => {
        update(
            'goods_receipts',
            { id: record.id, data: { status: newStatus }, previousData: record },
            {
                onSuccess: () => {
                    notify(`GRN ${label} successfully`, { type: 'success' });
                    refresh();
                },
                onError: () => notify('Error updating GRN status', { type: 'error' }),
            }
        );
    };

    const handleCreateVendorBill = () => {
        if (hasBill) return;
        navigate('/vendor_bills/create', {
            state: {
                record: {
                    grn_id: record.id,
                    po_id: record.po_id,
                    vendor_id: record.vendor_id,
                }
            }
        });
    };

    return (
        <div className="flex items-center gap-2">
            <EditButton />
            {(record.status === 'received' || record.status === 'qa_passed' || hasBill) && (
                <span title={hasBill ? 'A vendor bill has already been created from this GRN' : undefined}>
                    <Button
                        size="sm"
                        variant="outline"
                        className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                        onClick={handleCreateVendorBill}
                        disabled={hasBill}
                    >
                        <Receipt className="w-4 h-4 mr-2" />
                        {hasBill ? 'Vendor Bill Created' : 'Create Vendor Bill'}
                    </Button>
                </span>
            )}
            {record.status === 'draft' && (
                <Button
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    onClick={() => handleStatusChange('received', 'confirmed as received')}
                    disabled={isPending}
                >
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Confirm Receipt
                </Button>
            )}
            {record.status === 'received' && (
                <>
                    <Button
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        onClick={() => handleStatusChange('qa_passed', 'marked QA Passed')}
                        disabled={isPending}
                    >
                        <ShieldCheck className="w-4 h-4 mr-2" />
                        Pass QA
                    </Button>
                    <Button
                        size="sm"
                        variant="outline"
                        className="border-red-200 text-red-700 hover:bg-red-50"
                        onClick={() => handleStatusChange('qa_failed', 'marked QA Failed')}
                        disabled={isPending}
                    >
                        <ShieldX className="w-4 h-4 mr-2" />
                        Fail QA
                    </Button>
                </>
            )}
        </div>
    );
};

const GRNShowContent = () => {
    const record = useRecordContext<GoodsReceipt>();
    if (!record) return null;

    return (
        <div className="p-6 max-w-6xl mx-auto space-y-8">
            <div className="flex items-center gap-4 text-sm text-slate-500 border-b pb-4 flex-wrap">
                <StatusBadge status={record.status} />
                <span className="text-slate-300">|</span>
                <span className="flex items-center gap-1 font-medium text-slate-600">
                    <Truck className="w-4 h-4 text-slate-400" /> Received: <DateField source="received_date" />
                </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <Card>
                    <CardHeader>
                        <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-500">
                            Source Document
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-1">
                            <span className="text-xs font-bold text-slate-400 uppercase">Purchase Order</span>
                            <div>
                                <ReferenceField source="po_id" reference="purchase_orders" link="show">
                                    <TextField source="reference" className="text-blue-600 font-bold" />
                                </ReferenceField>
                            </div>
                        </div>
                        <div className="space-y-1">
                            <span className="text-xs font-bold text-slate-400 uppercase">Vendor</span>
                            <div>
                                <ReferenceField source="vendor_id" reference="vendors" link="show">
                                    <TextField source="name" className="text-slate-800 font-medium" />
                                </ReferenceField>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-500">
                            Notes
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-sm text-slate-700 whitespace-pre-wrap">{record.notes || <span className="italic text-slate-400">No notes provided.</span>}</p>
                    </CardContent>
                </Card>
            </div>
            
            <GRNItemsList />
        </div>
    );
};

export const GRNShow = () => (
    <Show title={<ShowTitle />} actions={<GRNShowActions />}>
        <GRNShowContent />
    </Show>
);
